import { v4 as uuidv4 } from 'uuid';
import { dbService } from '../db';
import { Document, DocumentHistory, DocumentVerification } from '../db/types';
import { toast } from 'sonner';

export const documentService = {
  async trackDocumentHistory(
    studentId: number,
    documentId: string,
    action: DocumentHistory['action'],
    details: Partial<Document>
  ): Promise<void> {
    try {
      const student = await dbService.getStudent(studentId);
      if (!student) {
        console.warn(`Cannot track document history: Student with ID ${studentId} not found`);
        return;
      }

      const documents = student.documents || [];
      const documentIndex = documents.findIndex(d => d.id === documentId);
      
      if (documentIndex === -1) {
        // Document not found - this might happen when adding new documents
        // Let's avoid throwing an error and just log this
        console.info(`Document ${documentId} not found in student records - might be a new document`);
        
        // For new documents on upload action, we can still proceed
        if (action === 'upload') {
          // Create a new document object if it doesn't exist
          const newDocument: Document = {
            id: documentId,
            name: details.name || 'Unknown document',
            type: details.type || 'application/octet-stream',
            uploadDate: new Date().toISOString(),
            uploadedBy: details.uploadedBy || 'system',
            verificationStatus: 'pending',
            url: details.url,
            history: [],
            verification: [],
            notes: details.notes
          };
          
          const history: DocumentHistory = {
            id: uuidv4(),
            documentId,
            uploadedBy: details.uploadedBy || 'system',
            uploadDate: new Date().toISOString(),
            documentName: newDocument.name,
            documentType: newDocument.type,
            action,
            previousUrl: null,
            newUrl: details.url,
            notes: details.notes || ''
          };
          
          newDocument.history = [history];
          documents.push(newDocument);
          await dbService.updateStudent(studentId, { documents });
          return;
        } else {
          // For other actions, we should have an existing document
          return;
        }
      }

      // Normal flow for existing documents
      const history: DocumentHistory = {
        id: uuidv4(),
        documentId,
        uploadedBy: details.uploadedBy || 'system',
        uploadDate: new Date().toISOString(),
        documentName: documents[documentIndex].name,
        documentType: documents[documentIndex].type,
        action,
        previousUrl: documents[documentIndex].url,
        newUrl: details.url,
        notes: details.notes || ''
      };

      documents[documentIndex] = {
        ...documents[documentIndex],
        history: [...(documents[documentIndex].history || []), history]
      };

      await dbService.updateStudent(studentId, { documents });
      console.log(`Document history tracked: ${action}`);
    } catch (error) {
      console.error('Error tracking document history:', error);
      // Only show toast for critical errors, not for expected scenarios
      if (error.message !== 'Document not found' && error.message !== 'Student not found') {
        toast.error('Failed to track document history');
      }
      throw error;
    }
  },

  async updateVerificationStatus(
    studentId: number,
    documentId: string,
    status: DocumentVerification['status'],
    verifiedBy: string,
    comments?: string
  ): Promise<void> {
    try {
      const student = await dbService.getStudent(studentId);
      if (!student) throw new Error('Student not found');

      const documents = student.documents || [];
      const documentIndex = documents.findIndex(d => d.id === documentId);
      
      if (documentIndex === -1) throw new Error('Document not found');

      const verification: DocumentVerification = {
        id: uuidv4(),
        documentId,
        status,
        verifiedBy,
        verificationDate: new Date().toISOString(),
        comments
      };

      documents[documentIndex] = {
        ...documents[documentIndex],
        verificationStatus: status,
        verification: [...(documents[documentIndex].verification || []), verification]
      };

      await dbService.updateStudent(studentId, { documents });
      toast.success(`Document verification status updated to ${status}`);
    } catch (error) {
      console.error('Error updating document verification:', error);
      toast.error('Failed to update document verification');
      throw error;
    }
  },

  async checkDocumentExpirations(studentId: number): Promise<void> {
    try {
      const student = await dbService.getStudent(studentId);
      if (!student || !student.documents) return;

      const today = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);

      for (const doc of student.documents) {
        if (doc.expirationDate) {
          const expDate = new Date(doc.expirationDate);
          if (expDate <= thirtyDaysFromNow && (!doc.expiration?.notificationSent)) {
            // Update notification status
            doc.expiration = {
              documentId: doc.id!,
              expirationDate: doc.expirationDate,
              notificationSent: true,
              notificationDate: new Date().toISOString()
            };
            
            toast.warning(`Document ${doc.name} will expire on ${expDate.toLocaleDateString()}`);
          }
        }
      }

      await dbService.updateStudent(studentId, { documents: student.documents });
    } catch (error) {
      console.error('Error checking document expirations:', error);
      toast.error('Failed to check document expirations');
    }
  },

  async replaceDocument(
    studentId: number,
    oldDocumentId: string,
    newDocument: Partial<Document>
  ): Promise<void> {
    try {
      const student = await dbService.getStudent(studentId);
      if (!student) throw new Error('Student not found');

      const documents = student.documents || [];
      const documentIndex = documents.findIndex(d => d.id === oldDocumentId);
      
      if (documentIndex === -1) throw new Error('Document not found');

      // Create new document with old document's history
      const newDoc: Document = {
        id: uuidv4(),
        name: newDocument.name || documents[documentIndex].name,
        type: newDocument.type || documents[documentIndex].type,
        uploadDate: new Date().toISOString(),
        url: newDocument.url,
        uploadedBy: newDocument.uploadedBy,
        verificationStatus: 'pending',
        history: documents[documentIndex].history || [],
        verification: [],
        expirationDate: newDocument.expirationDate,
        notes: newDocument.notes
      };

      // Track replacement in history
      await this.trackDocumentHistory(
        studentId,
        oldDocumentId,
        'replace',
        newDoc
      );

      // Replace old document with new one
      documents[documentIndex] = newDoc;

      await dbService.updateStudent(studentId, { documents });
      toast.success('Document replaced successfully');
    } catch (error) {
      console.error('Error replacing document:', error);
      toast.error('Failed to replace document');
      throw error;
    }
  }
};
